import React, { useCallback, useRef, useState } from 'react';
import { Formik, FormikErrors, FormikHelpers } from 'formik';

import { convertRequestErrorToMap } from '@tager/web-core';

import * as S from '@/components/modals/DetailedCarModal/components/Application/common.styles';
import { BASE_APPLICATION_TITLE } from '@/components/modals/DetailedCarModal/components/Application/common.constans';
import {
  BaseApplicationOption,
  BaseComponentProps,
} from '@/components/modals/DetailedCarModal/components/Application/common.types';
import RegistrationType from '@/components/modals/DetailedCarModal/components/Application/components/common/RegistationTypes';
import Request from '@/components/modals/DetailedCarModal/components/Application/components/ApplicationForCar/steps/Request';
import {
  handleValidateOrderForm,
  hasErrors,
} from '@/components/modals/DetailedCarModal/components/Application/common.helpers';
import { getInitialOptions } from '@/components/modals/DetailedCarModal/components/Application/components/ApplicationForCar/ApplicationForCar.constans';
import { submitOrderForm } from '@/components/modals/DetailedCarModal/components/Application/common.requests';
import { CarPreviewWrapper } from '@/components/modals/DetailedCarModal/components/Application/common.styles';
import CarPreview from '@/components/CarPreview';
import { googleEvent } from '@/utils/events';
import { DetailedCarOrderValues } from '@/services/leads/typings';
import useSettingItem from '@/hooks/useSettingItem';
import { useInvisibleCaptchaSubmit } from '@/components/YandexInvisibleCaptcha/hooks';
import YandexInvisibleCaptcha from '@/components/YandexInvisibleCaptcha';

import { getDefaultValues } from './ApplicationForCar.helper';

function ApplicationForCar({
  type,
  typeState,
  car,
  onSuccess,
}: BaseComponentProps) {
  const [options, setOptions] = useState<Array<BaseApplicationOption>>(
    getInitialOptions(typeState, type, car.hideCreditAndTradeIn)
  );
  const applicationGoogleEvent = useSettingItem(
    typeState === 'new'
      ? 'DETAILED_CAR_NEW_APPLICATION_GOOGLE_EVENT'
      : 'DETAILED_CAR_AMP_APPLICATION_GOOGLE_EVENT'
  );
  const applicationMyPriceGoogleEvent = useSettingItem(
    'DETAILED_CAR_AMP_MY_PRICE_GOOGLE_EVENT'
  );

  function onChange(option: BaseApplicationOption) {
    const newOptions = options.map((prevOption) => {
      if (prevOption.value === option.value) {
        return { ...prevOption, checked: !prevOption.checked };
      } else {
        return prevOption;
      }
    });
    setOptions(newOptions);
  }

  function submitGtagEvent(values: DetailedCarOrderValues) {
    if (values.myPrice && applicationMyPriceGoogleEvent) {
      googleEvent(applicationMyPriceGoogleEvent, car.googleEventParams);
    } else if (applicationGoogleEvent) {
      googleEvent(applicationGoogleEvent, car.googleEventParams);
    }
  }

  const formikHelpersRef = useRef<FormikHelpers<DetailedCarOrderValues> | null>(
    null
  );

  const submitOrderWithToken = useCallback(
    async (
      captchaValues: DetailedCarOrderValues & { captchaToken: string }
    ) => {
      const values = { ...captchaValues };
      const { captchaToken } = captchaValues;

      values.myPrice = values.myPrice
        ? Number(values.myPrice.toString().replaceAll(' ', ''))
        : null;
      values.creditAmount = values.creditAmount
        ? Number(values.creditAmount.toString().replaceAll(' ', ''))
        : null;
      values.myCarYear = values.myCarYear ? Number(values.myCarYear) : null;

      try {
        await submitOrderForm(
          {
            ...values,
            type: typeState,
            id: car.id,
            captchaToken,
          },
          {
            creditAmount: options[0]?.checked,
            carInCredit: options[1]?.checked,
          }
        );
        submitGtagEvent(values);
        onSuccess();
      } catch (error) {
        const errorMap = convertRequestErrorToMap(
          error as Error
        ) as FormikErrors<DetailedCarOrderValues>;
        formikHelpersRef.current?.setErrors(errorMap);
        console.error(error);
      } finally {
        formikHelpersRef.current?.setSubmitting(false);
      }
    },
    [
      applicationGoogleEvent,
      applicationMyPriceGoogleEvent,
      car,
      onSuccess,
      options,
      typeState,
    ]
  );

  const { captchaProps, handleSubmit: triggerCaptcha } =
    useInvisibleCaptchaSubmit(submitOrderWithToken);

  function onSubmit(
    values: DetailedCarOrderValues,
    formikHelper: FormikHelpers<DetailedCarOrderValues>
  ) {
    formikHelper.setSubmitting(true);
    formikHelpersRef.current = formikHelper;
    triggerCaptcha(values);
  }

  return (
    <S.Component>
      <S.StepTitle>{BASE_APPLICATION_TITLE[typeState][type]}</S.StepTitle>

      <CarPreviewWrapper>
        <CarPreview
          image={car.image}
          name={car.name}
          price={car.price}
          currency="BYN"
          body={car.bodyType}
          carType={typeState}
        />
      </CarPreviewWrapper>

      {options.length > 0 && (
        <RegistrationType
          applicationModalType={type}
          options={options}
          onChange={onChange}
        />
      )}

      <Formik<DetailedCarOrderValues>
        onSubmit={onSubmit}
        validate={(values) => handleValidateOrderForm(values, car.price ?? 0)}
        initialValues={getDefaultValues({ dealers: car.dealers ?? [] })}
        validateOnChange
      >
        {({
          handleSubmit,
          values,
          errors,
          handleChange,
          touched,
          handleBlur,
          setFieldValue,
          isSubmitting,
        }) => {
          return (
            <>
              <S.Form onSubmit={handleSubmit}>
                <Request
                  errors={errors}
                  values={values}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  options={options}
                  dealers={car.dealers ?? []}
                  isSubmitting={isSubmitting}
                  hasErrors={hasErrors(
                    handleValidateOrderForm(values, car.price ?? 0)
                  )}
                  setFieldValue={setFieldValue}
                />
              </S.Form>
              <YandexInvisibleCaptcha {...captchaProps} />
            </>
          );
        }}
      </Formik>
    </S.Component>
  );
}

export default ApplicationForCar;
